import React from 'react';
import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Layout } from "./components/layout/Layout";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { Dashboard } from "./pages/Dashboard";
import { Pipeline } from "./pages/Pipeline";
import { Projects } from "./pages/Projects";
import { ProjectDetail } from "./pages/ProjectDetail";
import { Transactions } from "./pages/Transactions";
import { EmployeeLedger } from "./pages/EmployeeLedger";
import { Calculator } from "./pages/Calculator";
import { Partnerships } from "./pages/Partnerships";
import { Reports } from "./pages/Reports";
import { FinancialReports } from "./pages/FinancialReports";
import { Ownership } from "./pages/Ownership";
import { Notes } from "./pages/Notes";
import { Settings } from "./pages/Settings";
import QBOSync from "./pages/QBOSync";
import { Login } from "./pages/Login";
import { AuthCallback } from "./pages/AuthCallback";
import { InvestorDashboard } from "./pages/InvestorDashboard";
import DealAnalysis from "./pages/DealAnalysis";
import PublicDealAnalysis from "./pages/PublicDealAnalysis";
import { ActivityHistory } from "./pages/ActivityHistory";
import { Partners } from "./pages/Partners";
import Documents from "./pages/Documents";
import ContractTemplates from "./pages/ContractTemplates";

// Smart redirect based on user role
const RoleBasedRedirect = () => {
  const { user, loading, isOwner } = useAuth();
  
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  
  // Redirect investors to their dashboard, owners to main dashboard
  return <Navigate to={isOwner ? "/dashboard" : "/investor"} replace />;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - detect during render (NOT in useEffect)
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=') || window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Smart redirect based on role */}
      <Route path="/" element={<RoleBasedRedirect />} />
      
      {/* Investor routes - accessible to investors */}
      <Route path="/investor" element={
        <ProtectedRoute>
          <Layout><InvestorDashboard /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Owner routes - require owner role */}
      <Route path="/dashboard" element={
        <ProtectedRoute requireOwner>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/pipeline" element={
        <ProtectedRoute requireOwner>
          <Layout><Pipeline /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute requireOwner>
          <Layout><Projects /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/transactions" element={
        <ProtectedRoute requireOwner>
          <Layout><Transactions /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ledger" element={
        <ProtectedRoute requireOwner>
          <Layout><EmployeeLedger /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ownership" element={
        <ProtectedRoute requireOwner>
          <Layout><Ownership /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/notes" element={
        <ProtectedRoute requireOwner>
          <Layout><Notes /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/proposals" element={
        <ProtectedRoute requireOwner>
          <Layout><Calculator /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/calculator" element={
        <Navigate to="/proposals" replace />
      } />
      <Route path="/deals" element={
        <Navigate to="/proposals" replace />
      } />
      <Route path="/partners" element={
        <ProtectedRoute requireOwner>
          <Layout><Partners /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute requireOwner>
          <Layout><Documents /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute requireOwner>
          <Layout><ContractTemplates /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/deal-analysis" element={
        <ProtectedRoute requireOwner>
          <Layout><DealAnalysis /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute requireOwner>
          <Layout><Reports /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/financial-reports" element={
        <Navigate to="/reports" replace />
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><Settings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/qbo-sync" element={
        <ProtectedRoute requireSuperOwner>
          <Layout><QBOSync /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/activity-history" element={
        <ProtectedRoute requireSuperOwner>
          <Layout><ActivityHistory /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Project detail - accessible to both but with filtered data */}
      <Route path="/projects/:id" element={
        <ProtectedRoute>
          <Layout><ProjectDetail /></Layout>
        </ProtectedRoute>
      } />

      {/* Public route - no auth required */}
      <Route path="/partner" element={<Partnerships />} />
      <Route path="/partnerships" element={<Navigate to="/partner" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public standalone page - no auth required, no layout */}
        <Route path="/calculator" element={<PublicDealAnalysis />} />
        {/* All other routes go through auth */}
        <Route path="*" element={
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
